import { buildApiUrl } from "../../../config.js";
import React, { useState, useEffect } from "react";
import { Bar, Pie, Line } from "react-chartjs-2";
import { ColorRing } from "react-loader-spinner";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import convertToCr from "../../ui/convertToCr";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const SalesReport = () => {
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [filtersApplied, setFiltersApplied] = useState({});
  const [userRole, setUserRole] = useState("");
  const [salesExecutiveId, setSalesExecutiveId] = useState("");
  const [salesExecutives, setSalesExecutives] = useState([]);

  const token = localStorage.getItem("token");

  const getUserInfoFromToken = () => {
    try {
      const payload = JSON.parse(atob(token.split(".")[1]));
      return { id: payload.id || payload._id, role: payload.role };
    } catch (error) {
      console.error("Error decoding token:", error);
      return { id: "", role: "" };
    }
  };

  const fetchSalesExecutives = async () => {
    try {
      const response = await fetch(
        buildApiUrl("/api/users/sales-executives"),
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const data = await response.json();
      setSalesExecutives(data.salesExecutives || []);
    } catch (error) {
      console.error("Error fetching sales executives:", error);
    }
  };

  const fetchReport = async () => {
    if (!salesExecutiveId) return;
    const url = new URL(
      `${buildApiUrl("/api/report/sales-executive-client-report/${salesExecutiveId}")}`
    );

    if (startDate) url.searchParams.append("startDate", startDate);
    if (endDate) url.searchParams.append("endDate", endDate);

    setLoading(true);
    try {
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await response.json();
      setReportData(data.data || []);
      setFiltersApplied(data.filtersApplied || {});
    } catch (error) {
      console.error("Error fetching report data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const userInfo = getUserInfoFromToken();
    setUserRole(userInfo.role);

    if (userInfo.role === "sales executive") {
      setSalesExecutiveId(userInfo.id);
    } else if (userInfo.role === "admin" || userInfo.role === "finance") {
      fetchSalesExecutives();
    }
  }, []);

  useEffect(() => {
    if (salesExecutiveId) {
      fetchReport();
    }
  }, [salesExecutiveId, startDate, endDate]);

  const filterPaymentHistory = (history) => {
    return history.filter((payment) => {
      const paymentDate = new Date(payment.date);
      return (
        (!startDate || paymentDate >= new Date(startDate)) &&
        (!endDate || paymentDate <= new Date(endDate))
      );
    });
  };

  const paymentModeData = () => {
    const paymentTotals = reportData
      .map((client) => filterPaymentHistory(client.paymentHistory))
      .flat()
      .reduce((acc, payment) => {
        acc[payment.paymentMode] =
          (acc[payment.paymentMode] || 0) + payment.amountPaid;
        return acc;
      }, {});

    return {
      labels: ["G-pay/PhonePe", "Bank", "Debit Card"],
      datasets: [
        {
          data: [
            paymentTotals["G-pay/PhonePe"] || 0,
            paymentTotals["Bank"] || 0,
            paymentTotals["Debit Card"] || 0,
          ],
          backgroundColor: ["#4F46E5", "#10B981", "#F59E0B"],
        },
      ],
    };
  };

  const outstandingData = {
    labels: reportData.map((client) => client.firmName),
    datasets: [
      {
        label: "Outstanding Amount",
        data: reportData.map((client) =>
          Math.abs(client.totalOutstandingAmount)
        ),
        backgroundColor: reportData.map((client) =>
          client.totalOutstandingAmount < 0 ? "#10B981" : "#EF4444"
        ),
      },
    ],
  };

  const paymentHistoryData = () => {
    const allPayments = reportData
      .map((client) => filterPaymentHistory(client.paymentHistory))
      .flat();

    const paymentsByDate = allPayments.reduce((acc, payment) => {
      const date = new Date(payment.date).toISOString().split("T")[0];
      acc[date] = (acc[date] || 0) + payment.amountPaid;
      return acc;
    }, {});

    const dates = Object.keys(paymentsByDate).sort();
    const amounts = dates.map((date) => paymentsByDate[date]);

    return {
      labels: dates,
      datasets: [
        {
          label: "Total Payments",
          data: amounts,
          fill: true,
          backgroundColor: "rgba(79, 70, 229, 0.2)",
          borderColor: "#4F46E5",
          tension: 0.3,
        },
      ],
    };
  };

  const totalOrders = reportData.reduce(
    (sum, client) => sum + client.orderCount,
    0
  );
  const totalPayments = reportData
    .map((client) => filterPaymentHistory(client.paymentHistory))
    .flat()
    .reduce((sum, payment) => sum + payment.amountPaid, 0);

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <ColorRing
          visible={true}
          height="80"
          width="80"
          ariaLabel="color-ring-loading"
          wrapperStyle={{}}
          wrapperClass="color-ring-wrapper"
          colors={["#e15b64", "#f47e60", "#f8b26a", "#abbd81", "#849b87"]}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen rounded-md bg-gray-100 p-2">
      <div className="space-y-2">
        {/* Header */}
        <div className="bg-white rounded-lg shadow p-4 grid grid-cols-1  items-center sm:grid-cols-[1fr_3fr]">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Sales Dashboard
            </h1>
            <div className="mt-1 text-gray-600 text-xs space-y-0.5">
              <p>
                Generated:{" "}
                {new Date().toLocaleDateString("en-IN", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                })}
              </p>
              <p className="italic">
                {startDate || endDate
                  ? `From ${startDate || "start"} to ${endDate || "now"}`
                  : "All time"}
              </p>
            </div>
          </div>
          <div className=" flex flex-col sm:flex-row gap-2">
            {(userRole === "admin" || userRole === "finance") && (
              <div className="w-full sm:w-1/3">
                <label className="block text-xs font-medium text-gray-700">
                  Sales Executive
                </label>
                <select
                  value={salesExecutiveId}
                  onChange={(e) => setSalesExecutiveId(e.target.value)}
                  className="mt-0.5 w-full rounded-md px-1 py-0.5 border-b border-indigo-400 shadow-sm focus:ring-1 focus:ring-indigo-400 focus:border-indigo-500 text-sm"
                >
                  <option value="">Select Executive</option>
                  {salesExecutives.map((exec) => (
                    <option key={exec.id} value={exec.id}>
                      {exec.name} ({exec.username})
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div className="w-full sm:w-1/3">
              <label className="block text-xs font-medium text-gray-700">
                Start Date
              </label>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="mt-0.5 w-full rounded-md px-1 py-0.5 border-b border-indigo-400 shadow-sm focus:ring-1 focus:ring-indigo-400 focus:border-indigo-500 text-sm"
              />
            </div>
            <div className="w-full sm:w-1/3">
              <label className="block text-xs font-medium text-gray-700">
                End Date
              </label>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="mt-0.5 w-full rounded-md px-1 py-0.5 border-b border-indigo-400 shadow-sm focus:ring-1 focus:ring-indigo-400 focus:border-indigo-500 text-sm"
              />
            </div>
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[
            { title: "Clients", value: reportData.length, color: "indigo" },
            { title: "Orders", value: totalOrders, color: "purple" },
            {
              title: "Payments",
              value: `₹${convertToCr(totalPayments)}`,
              color: "blue",
            },
          ].map((card, index) => (
            <div
              key={index}
              className={`bg-white rounded-lg shadow p-3 border-t-2 border-${card.color}-500 hover:shadow-md`}
            >
              <h3 className="text-sm font-semibold text-gray-700">
                {card.title}
              </h3>
              <p className={`mt-1 text-lg font-bold text-${card.color}-600`}>
                {card.value}
              </p>
            </div>
          ))}
        </div>

        {/* Charts */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr_3fr] gap-2">
          <div className="bg-white rounded-lg shadow p-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Payment Modes
            </h3>
            <div className="max-w-[200px] mx-auto flex items-center justify-center">
              <Pie
                data={paymentModeData()}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      position: "bottom",
                      labels: { font: { size: 10 } },
                    },
                  },
                }}
              />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Outstanding
            </h3>
            <Bar
              data={outstandingData}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top", labels: { font: { size: 10 } } },
                },
                scales: {
                  y: { beginAtZero: true, tick: { font: { size: 10 } } },
                },
              }}
            />
          </div>
          {/* Payment History Line Chart */}
          <div className="bg-white rounded-lg shadow p-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Payment History
            </h3>
            <Line
              data={paymentHistoryData()}
              options={{
                responsive: true,
                plugins: {
                  legend: { position: "top", labels: { font: { size: 10 } } },
                },
                scales: {
                  x: {
                    title: { display: true, text: "Date", font: { size: 10 } },
                    ticks: { font: { size: 10 } },
                  },
                  y: {
                    title: {
                      display: true,
                      text: "Amount (₹)",
                      font: { size: 10 },
                    },
                    beginAtZero: true,
                    ticks: { font: { size: 10 } },
                  },
                },
              }}
              height={100}
            />
          </div>
        </div>

        {/* Client Details */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="p-3">
            <h3 className="text-sm font-semibold text-gray-800 mb-2">
              Client Details
            </h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      "Firm Name",
                      "Contact",
                      "Orders",
                      "Payments",
                      "Outstanding",
                    ].map((header) => (
                      <th
                        key={header}
                        className="px-3 py-1 text-left text-gray-600 font-medium"
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {reportData.map((client) => {
                    const filteredPayments = filterPaymentHistory(
                      client.paymentHistory
                    );
                    const totalFilteredPayments = filteredPayments.reduce(
                      (sum, payment) => sum + payment.amountPaid,
                      0
                    );
                    return (
                      <tr key={client.clientId} className="hover:bg-indigo-50">
                        <td className="px-3 py-1 whitespace-nowrap text-gray-800 font-medium">
                          {client.firmName}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-gray-600">
                          {client.contactPerson}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-gray-700">
                          ₹{client.totalOrderAmount}
                        </td>
                        <td className="px-3 py-1 whitespace-nowrap text-gray-700">
                          ₹{totalFilteredPayments.toFixed(2)}
                        </td>
                        <td
                          className={`px-3 py-1 whitespace-nowrap font-semibold ${
                            client.totalOutstandingAmount < 0
                              ? "text-green-600"
                              : "text-red-600"
                          }`}
                        >
                          {client.totalOutstandingAmount < 0
                            ? `₹${Math.abs(client.totalOutstandingAmount)} adv`
                            : `₹${client.totalOutstandingAmount}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SalesReport;
